import React, { useState } from 'react';
import './Courses.css';

// ─── Data ─────────────────────────────────────────────────────────────────────

const whatsapp = '254727611362';

// Recorded courses — buy once, learn at your own pace
const recorded = [
  {
    id:      'copywriting',
    title:   'Copywriting',
    tagline: 'Words that sell.',
    hook:    'The internet runs on words. The right ones make money. Learn to write copy that moves people — for brands, social, emails, and beyond.',
    outcome: 'Walk away writing content people actually read — and businesses actually pay for.',
    accent:  '#2563EB',
    bg:      '#EFF6FF',
    price:   1200,
    format:  'Recorded',
    learns:  ['What copywriting is and why it pays', 'Writing for social media and brands', 'Email and ad copy fundamentals', 'Finding and pitching clients', 'Building a portfolio from scratch'],
  },
  {
    id:      'va',
    title:   'Virtual Assistance',
    tagline: 'Run businesses remotely.',
    hook:    'Entrepreneurs need people they can trust with their business. Become that person — skilled, reliable, and indispensable.',
    outcome: 'Leave with the tools, systems, and confidence to land your first VA client.',
    accent:  '#7C3AED',
    bg:      '#F5F3FF',
    price:   1200,
    format:  'Recorded',
    learns:  ['What a VA does and what clients expect', 'Tools every VA must know', 'Managing tasks, calendars, and emails', 'Finding clients on LinkedIn and Upwork', 'Pricing your services and getting paid'],
  },
  {
    id:      'social',
    title:   'Social Media Management',
    tagline: 'Presence is a product.',
    hook:    'Every brand needs a voice online. Learn to build, grow, and manage social pages that keep audiences engaged — and keep clients coming back.',
    outcome: 'Manage real accounts with a strategy, not guesswork.',
    accent:  '#059669',
    bg:      '#ECFDF5',
    price:   1200,
    format:  'Recorded',
    learns:  ['Platform strategy for Instagram, TikTok, X', 'Content planning and scheduling', 'Writing captions that actually perform', 'Growing pages organically', 'Reporting results to clients'],
  },
];

// Live sessions — booked by appointment, limited spots
const live = [
  {
    id:       'transcription',
    title:    'Transcription',
    tagline:  'Accuracy is a skill. Get paid for it.',
    hook:     'Audio and video content needs to be turned into text — every single day. Learn to do it fast, accurately, and profitably.',
    outcome:  'Transcribe professionally, pick a niche, and land your first paid job.',
    accent:   '#0891B2',
    bg:       '#ECFEFF',
    price:    900,
    duration: '2 sessions',
    schedule: 'Evenings, flexible',
    learns:   ['Transcription tools and shortcuts', 'How to transcribe accurately and fast', 'Different formats and niches', 'Where to find consistent paid work', 'How to price your services'],
  },
  {
    id:       'chat',
    title:    'Chat Moderation',
    tagline:  'Get paid to chat.',
    hook:     'Platforms pay people to manage conversations. Learn the accounts, rules, and techniques — and turn a chat window into an income stream.',
    outcome:  'Set up your accounts and make your first earnings within days.',
    accent:   '#0284C7',
    bg:       '#F0F9FF',
    price:    900,
    duration: '2 sessions',
    schedule: 'Evenings, from 8pm',
    learns:   ['Platform rules and requirements', 'How to chat professionally and consistently', 'Account types and setup', 'Proxies, payments, and withdrawals', 'Staying safe and scaling up'],
  },
  {
    id:       'ai',
    title:    'Train AI',
    tagline:  'Get paid to train AI models.',
    hook:     'AI companies pay people to make their models smarter. You can be one of them. No technical background needed — just attention to detail and consistency.',
    outcome:  'Open your accounts and complete your first paid task.',
    accent:   '#7C3AED',
    bg:       '#F5F3FF',
    price:    2000,
    duration: '3 sessions',
    schedule: 'Evenings, from 8pm',
    learns:   ['What AI training is and why it pays', 'Outlier, Prolific & Handshake — walkthroughs', 'How to open and verify accounts', 'Completing tasks to standard', 'Withdrawing your earnings'],
  },
  {
    id:       'leadgen',
    title:    'Lead Generation',
    tagline:  'Find clients before they find you.',
    hook:     'Every business needs customers. The person who finds those customers gets paid — well, and consistently. This is that skill.',
    outcome:  'Run your first outbound campaign and generate real leads.',
    accent:   '#D97706',
    bg:       '#FFFBEB',
    price:    2000,
    duration: '3 sessions',
    schedule: 'Evenings, from 8pm',
    learns:   ['What lead gen is and why businesses pay for it', 'Finding the right prospects', 'Cold outreach that gets responses', 'Tools that automate the grind', 'Turning leads into paying clients'],
  },
];

const perks = [
  { icon: '▶', label: 'Recorded — learn any time' },
  { icon: '◎', label: 'Live sessions by appointment' },
  { icon: '↗', label: 'Direct WhatsApp support' },
  { icon: '✦', label: 'Practical from day one' },
];

// ─── Components ───────────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3L5 8l5 5"/>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8h10M8 3l5 5-5 5"/>
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

// Recorded course card — price shown after outcome
function RecordedCard({ course }) {
  const [expanded, setExpanded] = useState(false);
  const msg = `Hi Brevian, I'd like to get the ${course.title} recorded course`;

  return (
    <div className="c-card c-card--recorded" style={{ '--ca': course.accent, '--cb': course.bg }}>
      <div className="c-card__type">
        <span className="c-dot" aria-hidden="true" />
        Recorded session
      </div>

      <div className="c-card__head">
        <p className="c-card__tagline">{course.tagline}</p>
        <h2 className="c-card__title">{course.title}</h2>
      </div>

      <p className="c-card__hook">{course.hook}</p>

      <div className="c-card__outcome">
        <span className="c-outcome__label">The outcome</span>
        <p className="c-outcome__text">{course.outcome}</p>
      </div>

      <button className="c-expand" onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
        {expanded ? 'Hide details' : 'What you\'ll learn'}
        <span className={`c-expand__chevron${expanded ? ' open' : ''}`}>›</span>
      </button>

      {expanded && (
        <ul className="c-learns-list">
          {course.learns.map(item => (
            <li key={item} className="c-learns-item">
              <span className="c-learns-dot" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      )}

      {/* Price revealed here — after hook, outcome, and learnings */}
      <div className="c-card__footer">
        <div className="c-price">
          <span className="c-price__amount">KES {course.price.toLocaleString()}</span>
          <span className="c-price__note">one-time · instant access</span>
        </div>
        <a
          href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(msg)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="c-btn c-btn--reserve"
          style={{ background: course.accent }}
        >
          Reserve a spot <ArrowIcon />
        </a>
      </div>
    </div>
  );
}

// Live session card
function LiveCard({ course }) {
  const [expanded, setExpanded] = useState(false);
  const msg = `Hi Brevian, I'd like to book a session for ${course.title}`;

  return (
    <div className="c-card c-card--live" style={{ '--ca': course.accent, '--cb': course.bg }}>
      <div className="c-card__type">
        <span className="c-dot c-dot--live" aria-hidden="true" />
        Live · By appointment
      </div>

      <div className="c-card__head">
        <p className="c-card__tagline">{course.tagline}</p>
        <h2 className="c-card__title">{course.title}</h2>
      </div>

      <div className="c-card__meta">
        <span className="c-tag"><ClockIcon />{course.duration}</span>
        <span className="c-tag">{course.schedule}</span>
      </div>

      <p className="c-card__hook">{course.hook}</p>

      <div className="c-card__outcome">
        <span className="c-outcome__label">The outcome</span>
        <p className="c-outcome__text">{course.outcome}</p>
      </div>

      <button className="c-expand" onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
        {expanded ? 'Hide details' : 'What you\'ll learn'}
        <span className={`c-expand__chevron${expanded ? ' open' : ''}`}>›</span>
      </button>

      {expanded && (
        <ul className="c-learns-list">
          {course.learns.map(item => (
            <li key={item} className="c-learns-item">
              <span className="c-learns-dot" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      )}

      <div className="c-card__footer">
        <div className="c-price">
          <span className="c-price__amount">KES {course.price.toLocaleString()}</span>
          <span className="c-price__note">per person · limited spots</span>
        </div>
        <a
          href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(msg)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="c-btn c-btn--reserve"
          style={{ background: course.accent }}
        >
          Reserve a spot <ArrowIcon />
        </a>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Courses() {
  return (
    <div className="courses-app">

      {/* Nav */}
      <header className="c-nav">
        <div className="c-container c-nav__inner">
          <a href="/" className="c-nav__back"><BackIcon /> Back to portfolio</a>
          <a
            href={`https://wa.me/${whatsapp}?text=Hi Brevian, I'd like to book a session`}
            target="_blank"
            rel="noopener noreferrer"
            className="c-btn c-btn--primary"
          >
            Book a session
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="c-hero">
        <div className="c-container">
          <p className="c-eyebrow">Courses · Brevian Emmanuel</p>
          <h1 className="c-hero__title">
            Skills that pay.<br />
            <em>Taught by someone<br />who earns with them.</em>
          </h1>
          <p className="c-hero__sub">
            No fluff. No theory for theory's sake. Every course is built around one thing — leaving with a skill you can use to make money, immediately.
          </p>
          <div className="c-perks">
            {perks.map(p => (
              <div key={p.label} className="c-perk">
                <span className="c-perk__icon">{p.icon}</span>
                <span>{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recorded */}
      <section className="c-section">
        <div className="c-container">
          <div className="c-section-head">
            <div>
              <p className="c-section-label">Recorded sessions</p>
              <h2 className="c-section-title">Learn at your own pace.</h2>
            </div>
            <p className="c-section-desc">Buy once. Watch as many times as you need. Pick it up when life allows.</p>
          </div>
          <div className="c-grid c-grid--3">
            {recorded.map(course => <RecordedCard key={course.id} course={course} />)}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="c-divider">
        <div className="c-container">
          <div className="c-divider__line" />
        </div>
      </div>

      {/* Live */}
      <section className="c-section">
        <div className="c-container">
          <div className="c-section-head">
            <div>
              <p className="c-section-label">Live sessions</p>
              <h2 className="c-section-title">Book a session. Show up. Earn.</h2>
            </div>
            <p className="c-section-desc">Small groups. Evenings. Book a slot, pay to confirm, and come ready to work.</p>
          </div>
          <div className="c-grid c-grid--2">
            {live.map(course => <LiveCard key={course.id} course={course} />)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="c-footer">
        <div className="c-container c-footer__inner">
          <p>© 2026 Brevian Emmanuel</p>
          <p>All sessions are virtual · Google Meet · WhatsApp support included</p>
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
            +254 727 611 362
          </a>
        </div>
      </footer>

    </div>
  );
}